import { useMemo } from "react";
import { Lock, ShieldCheck, AlertTriangle, Eye, Loader2 } from "lucide-react";
import { WhopCheckoutEmbed } from "@whop/checkout/react";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { isEmpty, fmtMoney, equivalent } from "@/lib/format";
import { payT } from "@/lib/i18n";
import { FlightsBox } from "@/components/FlightsBox";

const BLUE = "#2F5BFF";
const EMBED_ACCENT = { xcaret: "blue", nickelodeon: "orange", "hard-rock": "violet", barcelo: "indigo", "ava-resort": "pink", riu: "red", unico: "blue" };

const BRAND_LOGO = { nickelodeon: "/brand/nickelodeon-logo.png", riu: "/brand/riu-logo.png", "ava-resort": "/brand/ava-logo.png", barcelo: "/brand/barcelo-logo.png", unico: "/brand/unico-logo.png" };

const styleFor = (hotel) => {
  if (hotel.slug === "xcaret") return { accent: BLUE, bg: "#FBFAF6", mark: "/brand/xcaret-mark.png", emblem: "/brand/xcaret-emblem.png" };
  if (hotel.slug === "hard-rock") return { accent: "#5E4B9C", bg: "#FAFAFC", cardBg: "#F1EDF9", mark: null, emblem: "/brand/hardrock-logo.png", emblemCenter: true };
  return { accent: hotel.primary_color, bg: hotel.background_color, mark: null, emblem: hotel.logo_data || BRAND_LOGO[hotel.slug] || null, emblemCenter: !hotel.logo_data && !!BRAND_LOGO[hotel.slug] };
};

const dmy = (s) => {
  if (!s || String(s).trim() === "") return null;
  const d = new Date(`${s}T00:00:00`);
  if (isNaN(d)) return null;
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const guestsLine = (d, t) => {
  const parts = [];
  const add = (v, [s, p]) => { if (v !== null && v !== undefined) parts.push(`${v} ${v === 1 ? s : p}`); };
  add(d.adults, t.adult);
  add(d.minors, t.minor);
  add(d.infants, t.infant);
  return parts.length ? parts.join(", ") : null;
};

const Row = ({ label, value, testid }) => (value == null || value === "" ? null : (
  <div className="flex items-start justify-between gap-6 py-3.5">
    <span className="text-sm text-slate-500">{label}</span>
    <span className="text-right text-sm font-semibold uppercase tracking-wide text-slate-800" data-testid={testid}>{value}</span>
  </div>
));

const StripePay = ({ embed }) => {
  const stripe = useMemo(() => loadStripe(embed.publishable_key), [embed.publishable_key]);
  return (
    <div className="mt-6 min-h-[420px]" data-testid="stripe-embed">
      <EmbeddedCheckoutProvider stripe={stripe} options={{ clientSecret: embed.client_secret }}>
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    </div>
  );
};

const EmbeddedPay = ({ embed, guestName, accentName, t, lang }) => embed.kind === "stripe" ? <StripePay embed={embed} /> : (
  <div className="mt-6 min-h-[420px]" data-testid="whop-embed">
    <WhopCheckoutEmbed sessionId={embed.session_id} environment={embed.environment} returnUrl={embed.returnUrl}
      theme="light" locale={lang} hidePrice
      themeOptions={{ accentColor: accentName, backgroundColor: "#FFFFFF", borderRadius: 12, buttonText: t.payBtn }}
      prefill={{ ...(embed.email ? { email: embed.email } : {}), address: { ...(guestName ? { name: guestName } : {}), country: "MX" } }}
      fallback={<div className="flex items-center gap-2 py-10 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> {t.loadingPay}</div>} />
  </div>
);

export const XcaretCheckout = ({ hotel, data, preview = false, payState = {}, onPay, embed }) => {
  const { accent, bg, cardBg, mark, emblem, emblemCenter } = styleFor(hotel);
  const lang = hotel.language === "en" ? "en" : "es";
  const isAva = hotel.slug === "ava-resort";
  const t = payT(lang);
  const na = (v) => (isEmpty(v) ? null : String(v));
  const curr = data.currency || "USD";
  const num = data.amount_cents == null ? null : fmtMoney(data.amount_cents, curr).split(" ").slice(1).join(" ");
  const connected = !preview && !payState.pending;
  const statusNotice = !payState.pending && payState.tone !== "ok" && payState.notice;

  return (
    <div className="min-h-full" style={{ background: bg, fontFamily: "'Plus Jakarta Sans', sans-serif" }} data-testid={`payment-template-${hotel.slug}`}>
      {preview && (
        <div className="flex items-center justify-center gap-2 bg-[#FFD84D] py-2 pl-4 pr-12 text-center text-xs font-bold uppercase tracking-wider text-[#2B2200]" data-testid="payment-preview-mode-badge">
          <Eye size={14} /> {t.preview}
        </div>
      )}
      <header className="border-b border-slate-200/70 px-6 py-5 sm:px-10">
        <div className="mx-auto flex max-w-6xl items-center gap-3">
          {mark && <img src={mark} alt="" className="h-9 w-9 object-contain" data-testid="pay-header-mark" />}
          {hotel.logo_data
            ? <img src={hotel.logo_data} alt={hotel.display_name} className="h-9 max-w-[200px] object-contain" data-testid="pay-hotel-logo" />
            : <span className="text-xl font-semibold tracking-wide text-slate-900 sm:text-2xl" data-testid="pay-hotel-name" style={{ fontFamily: "'Playfair Display', serif" }}>{hotel.display_name}</span>}
        </div>
      </header>

      <main className="mx-auto grid max-w-6xl gap-8 px-6 py-10 sm:px-10 lg:grid-cols-2 lg:gap-12 lg:py-16">
        <section>
          <p className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: accent }} data-testid="pay-heading">{t.finish}</p>
          <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_18px_50px_-30px_rgba(0,0,0,0.3)]" style={cardBg ? { background: cardBg, borderColor: cardBg } : undefined} data-testid="pay-reservation-card">
            <div className="px-7 pt-7 sm:px-9">
              {emblem && <img src={emblem} alt="" className={emblemCenter ? "mx-auto mb-6 h-28 max-w-[260px] object-contain sm:h-32" : "mb-5 h-20 max-w-[200px] object-contain sm:h-24"} data-testid="pay-emblem" />}
              <h1 className={`text-3xl font-semibold sm:text-[2rem] ${isAva ? "" : "text-slate-900"}`} style={{ fontFamily: "'Playfair Display', serif", color: isAva ? "#6E6E69" : undefined }}>{t.title}</h1>
              {isAva && (
                <div className="mt-3" data-testid="pay-ava-tagline" style={{ color: accent }}>
                  <p className="text-xl font-medium sm:text-2xl" style={{ fontFamily: "'Playfair Display', serif" }}>Oceanfront Promise <span className="align-super text-xs">™</span></p>
                  <p className="mt-1 text-sm leading-relaxed">An exclusive commitment that guarantees every guest an ocean view from their room, villa, or suite</p>
                </div>
              )}
            </div>
            <div className="divide-y px-7 py-4 sm:px-9" style={{ borderColor: cardBg ? "rgba(94,75,156,0.12)" : undefined }}>
              <Row label="Hotel" value={na(data.property_name)} testid="pay-property" />
              <Row label={t.guest} value={na(data.guest_name)} testid="pay-guest-name" />
              <Row label={t.guests} value={guestsLine(data, t)} testid="pay-guests" />
              <Row label={t.room} value={na(data.room)} testid="pay-room" />
              <Row label={t.transport} value={na(data.transportation)} testid="pay-transportation" />
              <Row label={t.extras} value={na(data.additional_services)} testid="pay-additional-services" />
              <Row label={t.credits} value={data.resort_credits && String(data.resort_credits).trim() ? `USD ${data.resort_credits}` : null} testid="pay-resort-credits" />
              <Row label={t.checkIn} value={dmy(data.check_in)} testid="pay-check-in" />
              <Row label={t.checkOut} value={dmy(data.check_out)} testid="pay-check-out" />
              {data.total_cents != null && <>
                <Row label={t.total} value={fmtMoney(data.total_cents, curr)} testid="pay-total-amount" />
                <Row label={t.initial} value={data.initial_cents == null ? null : fmtMoney(data.initial_cents, curr)} testid="pay-initial-payment" />
                <Row label={t.balance} value={data.balance_cents == null ? null : fmtMoney(data.balance_cents, curr)} testid="pay-balance" />
              </>}
            </div>
            <FlightsBox flights={data.flights} lang={lang} accent={accent} />
            <div className="mx-7 mb-7 flex items-end justify-between border-t border-slate-200 pt-6 sm:mx-9">
              <span className="text-base text-slate-500">{t.totalLabel}</span>
              <span className="flex items-baseline gap-1.5">
                <span className="text-4xl font-bold" style={{ color: accent, fontFamily: "'Playfair Display', serif" }} data-testid="pay-amount">{num == null ? t.na : `$${num}`}</span>
                <span className="text-sm font-semibold text-slate-400" data-testid="pay-currency">{curr}</span>
              </span>
            </div>
            {equivalent(data.amount_cents, curr, data.exchange_rate) && (
              <p className="-mt-4 mb-7 px-7 text-right text-xs text-slate-500 sm:px-9" data-testid="pay-equivalent">
                {t.equiv} {equivalent(data.amount_cents, curr, data.exchange_rate)} · {t.rate} {data.exchange_rate} MXN/USD
              </p>
            )}
          </div>
        </section>

        <section>
          <div className="rounded-2xl border border-slate-200 bg-white p-7 shadow-[0_18px_50px_-30px_rgba(0,0,0,0.3)] sm:p-9">
            <h2 className="text-lg font-semibold text-slate-900">{t.secure}</h2>

            {embed && !preview ? <EmbeddedPay embed={embed} guestName={data.guest_name} accentName={EMBED_ACCENT[hotel.slug] || "blue"} t={t} lang={lang} /> : payState.tone === "ok" ? (
              <div className="mt-6 flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800" data-testid="pay-notice">
                <ShieldCheck size={20} className="mt-0.5 shrink-0" /><span>{payState.notice}</span>
              </div>
            ) : statusNotice ? (
              <div className="mt-6 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="pay-notice">
                <AlertTriangle size={20} className="mt-0.5 shrink-0" /><span>{payState.notice}</span>
              </div>
            ) : (
              <div className="mt-6 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-5" data-testid="pay-card-placeholder">
                <div className="flex items-start gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg text-white" style={{ background: accent }}><Lock size={18} /></span>
                  <div>
                    <p className="text-sm font-semibold text-slate-800">
                      {connected ? t.loadingForm : t.pendingProc}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">{t.cardNote}</p>
                  </div>
                </div>
              </div>
            )}

            {!(embed && !preview) && <><button data-testid="public-pay-button" disabled={preview || payState.disabled} onClick={onPay}
              className="mt-6 w-full rounded-xl px-6 py-4 text-base font-bold text-white transition-[transform,opacity] duration-200 enabled:hover:-translate-y-0.5 enabled:active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-50"
              style={{ background: accent }}>
              {payState.loading ? t.loading : payState.notice && !payState.disabled ? t.retry : payState.label || t.continue}
            </button>
            <p className="mt-4 flex items-center justify-center gap-1.5 text-center text-xs text-slate-400">
              <Lock size={12} /> {t.secureConn}
            </p></>}
          </div>
        </section>
      </main>
    </div>
  );
};
